import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, ClipboardCheck, Trash2, Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useFleetStore } from '@/stores/fleet';
import { checklistsApi, CHECKLIST_TEMPLATES, type Checklist, type ChecklistItem } from '@/api/endpoints/checklists';
import { cn, formatDateTime } from '@/lib/utils';

export function ChecklistPage() {
  const query = useQuery({ queryKey: ['checklists'], queryFn: () => checklistsApi.list() });
  const [creating, setCreating] = useState(false);
  const records = query.data ?? [];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Checklist Náutico</h1>
          <p className="text-sm text-muted-foreground">
            Vistoria de saída, retorno, segurança e inspeção — com itens, observação e histórico.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Novo checklist</Button>
      </div>

      {creating && <ChecklistEditor onClose={() => setCreating(false)} />}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando checklists…</p>
      ) : query.error ? (
        <p className="text-sm text-destructive">Erro ao carregar (faça login).</p>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <ClipboardCheck className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum checklist registrado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">{records.map((r) => <ChecklistCard key={r.id} record={r} />)}</div>
      )}
    </div>
  );
}

function ChecklistEditor({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const devices = useFleetStore((s) => s.devices);
  const sorted = useMemo(() => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')), [devices]);

  const [deviceId, setDeviceId] = useState<number | ''>(sorted[0]?.id ?? '');
  const [templateKey, setTemplateKey] = useState(CHECKLIST_TEMPLATES[0].key);
  const template = CHECKLIST_TEMPLATES.find((t) => t.key === templateKey)!;
  const [items, setItems] = useState<ChecklistItem[]>(template.items.map((label) => ({ label, ok: true })));
  const [note, setNote] = useState('');

  const onTemplateChange = (key: string) => {
    setTemplateKey(key);
    const t = CHECKLIST_TEMPLATES.find((x) => x.key === key)!;
    setItems(t.items.map((label) => ({ label, ok: true })));
  };
  const toggle = (i: number) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ok: !it.ok } : it)));
  const allOk = items.every((i) => i.ok);

  const save = useMutation({
    mutationFn: () => {
      const dev = devices.get(Number(deviceId));
      return checklistsApi.create({
        device_id: Number(deviceId), device_name: dev?.name ?? `Embarcação ${deviceId}`,
        template: template.label, items, note: note.trim() || null, approved: allOk,
      });
    },
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['checklists'] }); onClose(); },
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Embarcação</span>
            <select value={deviceId} onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : '')} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              {sorted.length === 0 && <option value="">Nenhuma embarcação</option>}
              {sorted.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Tipo</span>
            <select value={templateKey} onChange={(e) => onTemplateChange(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              {CHECKLIST_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
        </div>

        <ul className="divide-y divide-border rounded-lg border border-border">
          {items.map((it, i) => (
            <li key={it.label} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="text-sm">{it.label}</span>
              <button type="button" onClick={() => toggle(i)} className={cn('inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold', it.ok ? 'bg-status-running/10 text-status-running' : 'bg-status-blocked/10 text-status-blocked')}>
                {it.ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {it.ok ? 'OK' : 'Não OK'}
              </button>
            </li>
          ))}
        </ul>

        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observação (opcional)" />
        <div className="flex items-center justify-between gap-2">
          <span className={cn('rounded-full px-3 py-1 text-xs font-bold', allOk ? 'bg-status-running/10 text-status-running' : 'bg-status-ready/10 text-status-ready')}>
            {allOk ? 'Aprovado' : 'Reprovado (itens pendentes)'}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => save.mutate()} disabled={!deviceId || save.isPending} className="gap-1.5"><Check className="h-4 w-4" /> {save.isPending ? 'Salvando…' : 'Salvar'}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChecklistCard({ record }: { record: Checklist }) {
  const queryClient = useQueryClient();
  const del = useMutation({
    mutationFn: () => checklistsApi.remove(record.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checklists'] }),
  });
  const okCount = record.items.filter((i) => i.ok).length;
  const pendentes = record.items.filter((i) => !i.ok);

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold">
              <Link to={`/devices/${record.device_id}`} className="hover:underline">{record.device_name}</Link>
              <span className="ml-2 text-xs font-normal text-muted-foreground">· {record.template}</span>
            </p>
            <p className="text-xs text-muted-foreground">{record.created_at ? formatDateTime(record.created_at) : ''}{record.created_by ? ` · ${record.created_by}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', record.approved ? 'bg-status-running/10 text-status-running' : 'bg-status-blocked/10 text-status-blocked')}>
              {record.approved ? 'Aprovado' : 'Reprovado'}
            </span>
            <button type="button" onClick={() => { if (window.confirm('Excluir este checklist?')) del.mutate(); }} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {okCount}/{record.items.length} itens OK
          {pendentes.length > 0 && <span className="text-status-blocked"> · Pendentes: {pendentes.map((i) => i.label).join(', ')}</span>}
        </p>
        {record.note && <p className="text-xs italic text-foreground/80">"{record.note}"</p>}
      </CardContent>
    </Card>
  );
}
